import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler, OptionType} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isFalse} from 'ramda-adjunct';

import {IssuedType} from '@dms/api/core';
import {useGetTaxDocumentSettingQuery, useUpdateTaxDocumentSettingMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, NullishBoolean, useNavigate, useRequiredParams} from 'shared/web';

type FormValues = {
  branchId: string | null;
  issuedType: IssuedType;
};

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.accountingSettings.labels.issuedType'),
    href: settingsRoutes.taxDocumentIssuedType,
  },
];

export function TaxDocumentForPaymentIssuedTypeDetail() {
  const {id: issuedTypeId} = useRequiredParams();
  const navigate = useNavigate();

  const {
    data: issuedTypeDefinition,
    isLoading: isLoadingDefinition,
    isError: isDefinitionError,
  } = useGetTaxDocumentSettingQuery({id: issuedTypeId});
  const [updateIssuedTypeDefinition] = useUpdateTaxDocumentSettingMutation();

  const {
    data: branches,
    branchOptions,
    isLoading: isLoadingBranches,
    isError: isBranchesError,
  } = useBranches();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await updateIssuedTypeDefinition({
      updateTaxDocumentSettingRequestBody: data,
      id: issuedTypeId,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.taxDocumentIssuedType);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const isLoading = isLoadingBranches || isLoadingDefinition;
  const isError = isBranchesError || isDefinitionError;

  const branchName =
    branches?.branchListItems.find((branch) => branch.id === issuedTypeDefinition?.branchId)
      ?.marketingName ?? i18n.t('page.accounting.labels.tenantDefinition');

  const title = `${branchName} - ${i18n.t(`page.accountingSettings.labels.${issuedTypeDefinition?.issuedType}`)}`;

  const defaultValues = issuedTypeDefinition
    ? {
        branchId: issuedTypeDefinition.branchId,
        issuedType: issuedTypeDefinition.issuedType,
      }
    : undefined;

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs,
        }}
        data-testid={testIds.settings.taxDocumentIssuedTypeDetail('page')}
        isLoading={isLoading}
        isError={isError}
      >
        <Form<FormValues>
          onSubmit={handleSubmit}
          experimentalZodSchema={getSchema(issuedTypeDefinition?.isSystem)}
          defaultValues={defaultValues}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, _navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    isNotClearable
                    options={branchOptions}
                    label={i18n.t('entity.cashRegister.parameters.branch')}
                    isRequired
                    isDisabled={issuedTypeDefinition?.isSystem}
                    data-testid={testIds.settings.taxDocumentIssuedTypeDetail('branch')}
                    placeholder={
                      issuedTypeDefinition?.isSystem
                        ? i18n.t('page.accounting.labels.tenantDefinition')
                        : undefined
                    }
                  />
                  <FormField
                    control={control}
                    type="choice"
                    options={issuedTypeOptions}
                    name="issuedType"
                    data-testid={testIds.settings.taxDocumentIssuedTypeDetail('issuedType')}
                    isNotClearable
                    isRequired
                    label={i18n.t('page.accountingSettings.labels.issuedType')}
                  />
                </Grid>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () => navigate(settingsRoutes.taxDocumentIssuedType),
                    variant: 'secondary',
                    'data-testid': testIds.settings.taxDocumentIssuedTypeDetail('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                    'data-testid': testIds.settings.taxDocumentIssuedTypeDetail('save'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const getSchema = (isSystem: NullishBoolean) => {
  const branchIdSchema = isFalse(isSystem) ? z.string().nonempty() : z.string().nullable();

  return z.object({
    branchId: branchIdSchema,
    issuedType: z.enum(['automatic', 'manual', 'not_create'] as const),
  });
};

const issuedTypeOptions: OptionType<IssuedType>[] = [
  {value: 'automatic', label: i18n.t('page.accountingSettings.labels.automatic')},
  {value: 'manual', label: i18n.t('page.accountingSettings.labels.manual')},
  {value: 'not_create', label: i18n.t('page.accountingSettings.labels.not_create')},
];
